'use client';

import Image from 'next/image';
import { useLocale } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ContentWrapper } from '@/components/ContentWrapper';
import { SafeHTML } from '@/components/SafeHTML';
import SectionTitle from '@/components/SectionTitle';
import { IMAGES_ASSETS_PREFIX } from '@/constants';
import type { Meilisearch_ServiceBlock } from '@/lib/api/meilisearch-types';
import type { PossibleLocales } from '@/utils/AppConfig';

interface ServicesPageContentProps {
  sections: Meilisearch_ServiceBlock[];
}

export const ServicesPageContent: React.FC<ServicesPageContentProps> = ({
  sections,
}) => {
  const locale = useLocale() as PossibleLocales;
  const [activeSection, setActiveSection] = useState<string | undefined>(
    sections[0]![locale]?.titre,
  );
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      let foundActiveSection = false;
      refs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          if (rect.top <= 300 && rect.bottom >= 300 && !foundActiveSection) {
            setActiveSection(sections[index]?.slug);
            foundActiveSection = true;
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Trigger on mount to set the initial active section

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <ContentWrapper rootClassName="bg-slate-50 relative">
      {/* <div className="my-6 text-center text-5xl">Services</div> */}
      <div className="relative mx-auto flex max-w-screen-sm gap-24">
        {/* Contenu principal */}
        <div className="relative flex grow flex-col gap-12">
          {sections.map((elt, index) => (
            <div
              key={`content-${elt?.slug}`}
              ref={(el) => {
                refs.current[index] = el;
              }} // Utilisation de la ref dynamique pour chaque section
              className="relative h-fit"
            >
              <div id={elt?.slug} className="absolute top-[-200px]" />

              <SectionTitle
                rootClassName="sticky top-[var(--eo-header-height)] bg-slate-50 z-[3]"
                className={twMerge(
                  activeSection !== elt?.slug ? 'text-gray-600' : '',
                )}
              >
                {elt![locale]?.titre}
              </SectionTitle>
              {/* <h2 className="text-4xl font-bold">{elt![locale]?.titre}</h2> */}
              <SafeHTML html={elt![locale]?.contenu} className="text-base" />
              {elt.cover && (
                <div className={twMerge('relative aspect-[8/3] w-full')}>
                  <Image
                    src={`${IMAGES_ASSETS_PREFIX}/${elt.cover}`}
                    alt={elt![locale]?.titre}
                    fill
                    className="object-cover"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Menu latéral */}
        <aside className="sticky right-0 top-[var(--eo-header-height)] hidden size-fit min-w-48 sm:block">
          <ul className="menu">
            {sections.map((elt) => (
              <li className="mb-0" key={`menu-itm-${elt?.slug}`}>
                <a
                  href={`#${elt?.slug}`}
                  className={twMerge(
                    'w-full',
                    activeSection === elt?.slug
                      ? 'bg-secondary-200 text-black'
                      : 'text-gray-500',
                  )}
                >
                  {elt![locale]?.titre}
                </a>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </ContentWrapper>
  );
};
